import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Trophy, ChevronRight, Crown, Award, Sparkles, Handshake, TrendingUp, Loader2, ArrowRight,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageT } from '../i18n/usePageT';
import { SEO } from '../components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const podiumIcons = [Crown, Trophy, Award];
const podiumColors = [
  'bg-gradient-to-br from-[#E9C46A] to-[#E76F51]',  // 1st
  'bg-gradient-to-br from-slate-300 to-slate-500',  // 2nd
  'bg-gradient-to-br from-amber-600 to-amber-800',  // 3rd
];

export const AffiliateLeaderboardPage = () => {
  const { language } = useLanguage();
  const T = usePageT();

  const [data, setData] = useState({ results: [], month_label: null });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/affiliates/leaderboard?period=${period}&limit=10`);
        setData(data);
      } catch (err) {
        setData({ results: [], month_label: null });
      } finally {
        setLoading(false);
      }
    })();
  }, [period]);

  const top = data.results.slice(0, 3);
  const rest = data.results.slice(3);
  const maxEarned = data.results[0]?.earned_this_period || 1;

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="affiliate-leaderboard-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F4C5C] via-[#0B3A47] to-[#0F4C5C] text-white py-16">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-4">
            <Link to="/" className="hover:text-white">{T('Accueil', 'Home')}</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/affiliate-program" className="hover:text-white">{T('Programme partenaire', 'Partner program')}</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#E9C46A]">{T('Classement', 'Leaderboard')}</span>
          </nav>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-[#E9C46A]/15 border border-[#E9C46A]/30 rounded-full px-4 py-1.5 mb-5">
              <Trophy className="w-4 h-4 text-[#E9C46A]" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#E9C46A]">{T('Hall of fame', 'Hall of fame')}</span>
            </span>
            <SEO
              title={T('Top affiliés du mois', 'Top affiliates of the month')}
              description={T(
                'Les partenaires qui transforment leur audience en revenus. Rejoignez-les avec votre code unique.',
                'The partners turning their audience into revenue. Join them with your unique code.'
              )}
              url={`${window.location.origin}/affiliates/leaderboard`}
            />
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-3">
              {T('Top affiliés ', 'Top affiliates ')}
              <span className="text-[#E9C46A]">{T('du mois', 'of the month')}</span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl">
              {T(
                'Les partenaires qui transforment leur audience en revenus. Rejoignez-les avec votre code unique.',
                'The partners turning their audience into revenue. Join them with your unique code.',
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="container-main py-12">
        {/* Period switcher */}
        <Tabs value={period} onValueChange={setPeriod} className="mb-8 flex justify-center">
          <TabsList>
            <TabsTrigger value="month" data-testid="lb-tab-month">
              {T('Ce mois', 'This month')} {data.month_label ? `(${data.month_label})` : ''}
            </TabsTrigger>
            <TabsTrigger value="all_time" data-testid="lb-tab-all-time">
              {T('Toutes périodes', 'All-time')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-[#0F4C5C]" /></div>
        ) : data.results.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#0F4C5C]/10" data-testid="lb-empty">
            <Sparkles className="w-14 h-14 mx-auto text-[#0F4C5C]/30 mb-4" />
            <h2 className="text-xl font-bold text-[#0F4C5C] mb-2">
              {T('Le classement démarre ce mois-ci !', 'The leaderboard kicks off this month!')}
            </h2>
            <p className="text-sm text-[#0F4C5C]/70 mb-6 max-w-md mx-auto">
              {T('Soyez le premier à apparaître ici. Rejoignez le programme partenaire et commencez à partager vos liens.',
                 'Be the first to appear here. Join the partner program and start sharing your links.')}
            </p>
            <Link to="/affiliate-program">
              <Button className="btn-primary" data-testid="lb-cta-join">
                <Handshake className="w-4 h-4 mr-2" />
                {T('Devenir partenaire', 'Become a partner')}
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Podium top 3 */}
            {top.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-4xl mx-auto" data-testid="lb-podium">
                {/* Render so the #1 is in the middle on desktop */}
                {[top[1], top[0], top[2]].filter(Boolean).map((entry, displayIdx) => {
                  const realRank = entry.rank;
                  const PodiumIcon = podiumIcons[realRank - 1] || Award;
                  const isFirst = realRank === 1;
                  return (
                    <Card
                      key={`${entry.rank}-${entry.display_name}`}
                      className={`border-2 ${isFirst ? 'border-[#E9C46A] shadow-xl md:scale-105' : 'border-[#0F4C5C]/10'} relative overflow-hidden`}
                      data-testid={`lb-podium-rank-${realRank}`}
                    >
                      <div className={`absolute top-0 left-0 right-0 h-2 ${podiumColors[realRank - 1] || 'bg-slate-300'}`} />
                      <CardContent className="p-6 text-center pt-8">
                        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${podiumColors[realRank - 1] || 'bg-slate-300'} mb-3`}>
                          <PodiumIcon className="w-8 h-8 text-white" />
                        </div>
                        <p className="text-xs text-[#0F4C5C]/60 uppercase tracking-widest mb-1">#{realRank}</p>
                        <h3 className="text-xl font-bold text-[#0F4C5C] mb-1">{entry.display_name}</h3>
                        <Badge variant="outline" className={`mb-3 text-xs ${entry.tier === 'premium' ? 'border-[#E9C46A] text-[#0F4C5C] bg-[#E9C46A]/10' : ''}`}>
                          {entry.tier === 'premium' ? (<><Crown className="w-3 h-3 mr-1" />Premium</>) : (<>Standard</>)}
                        </Badge>
                        <div className="border-t border-[#0F4C5C]/10 pt-3 mt-3">
                          <p className="text-xs text-[#0F4C5C]/60">{T('Gains', 'Earned')}</p>
                          <p className="text-3xl font-bold text-emerald-700">€{entry.earned_this_period.toFixed(2)}</p>
                          <p className="text-xs text-[#0F4C5C]/60 mt-1">
                            {entry.conversions_this_period} {T('ventes', 'sales')}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Rest of the ranking */}
            {rest.length > 0 && (
              <Card className="max-w-3xl mx-auto border-[#0F4C5C]/10" data-testid="lb-rest-list">
                <CardHeader>
                  <CardTitle className="text-base text-[#0F4C5C]">
                    {T('Suite du classement', 'Rest of the ranking')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {rest.map((entry) => (
                      <div
                        key={`${entry.rank}-${entry.display_name}`}
                        className="flex items-center gap-3 p-3 rounded-lg bg-[#FBF8F3]"
                        data-testid={`lb-row-${entry.rank}`}
                      >
                        <div className="w-9 h-9 rounded-full bg-[#0F4C5C]/10 flex items-center justify-center font-bold text-[#0F4C5C] flex-shrink-0">
                          #{entry.rank}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#0F4C5C] truncate">{entry.display_name}</p>
                          <div className="flex items-center gap-2 text-xs text-[#0F4C5C]/60">
                            <span>{entry.conversions_this_period} {T('ventes', 'sales')}</span>
                            {entry.tier === 'premium' && <Crown className="w-3 h-3 text-[#E9C46A]" />}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="hidden sm:block w-32 h-2 bg-[#0F4C5C]/10 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#2A9D8F] to-[#E9C46A]"
                              style={{ width: `${Math.max(5, (entry.earned_this_period / maxEarned) * 100)}%` }}
                            />
                          </div>
                          <p className="text-emerald-700 font-bold text-sm w-24 text-right">€{entry.earned_this_period.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* CTA bottom */}
        <Card className="mt-10 max-w-3xl mx-auto bg-gradient-to-br from-[#2A9D8F] to-[#0F4C5C] text-white border-0">
          <CardContent className="p-8 text-center">
            <TrendingUp className="w-10 h-10 mx-auto text-[#E9C46A] mb-3" />
            <h3 className="text-2xl font-bold mb-2">
              {T('Votre place dans ce classement vous attend.', 'Your spot in this ranking is waiting.')}
            </h3>
            <p className="text-white/80 mb-5 max-w-xl mx-auto">
              {T('Inscription gratuite, code unique généré instantanément, jusqu\'à 12% de commission.',
                 'Free registration, unique code generated instantly, up to 12% commission.')}
            </p>
            <Link to="/affiliate-program">
              <Button className="bg-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/90 font-bold px-8 py-6" data-testid="lb-cta-bottom">
                {T('Rejoindre le programme', 'Join the program')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
